'use client';

import { useEffect, useState } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import Image from 'next/image';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { Typography } from '@/components/ui/typography';
import { SectionHeading } from '@/components/frontend/common/section-heading';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { cn } from '@/lib/utils';

interface IntroSectionProps {
    translationNamespace: string;
    paragraphKeys?: string[];
    imageSrc: string;
    imageAlt?: string;
    sectionHeadingPage?: string;
    sectionHeadingTitle?: string;
    sectionHeadingPill?: string;
    pageKey?: string;
    backendDescription?: string;
}

export function IntroSection({
    translationNamespace,
    paragraphKeys = ['paragraph1', 'paragraph2'],
    imageSrc,
    imageAlt,
    sectionHeadingPage,
    sectionHeadingTitle,
    sectionHeadingPill,
    pageKey,
    backendDescription
}: IntroSectionProps) {
    const t = useTranslations(translationNamespace);
    const locale = useLocale();
    const sectionRef = useScrollAnimation({ threshold: 0.1 });
    const [apiDescription, setApiDescription] = useState<string | null>(backendDescription || null);
    const [apiTitle, setApiTitle] = useState<string | null>(null);
    const [isLoadingApi, setIsLoadingApi] = useState(true);

    // Fetch feature data from backend API if pageKey is provided
    useEffect(() => {
        const fetchFeatureData = async () => {
            if (!pageKey) {
                setIsLoadingApi(false);
                return;
            }

            setIsLoadingApi(true);

            // If backendDescription is provided as prop and is not null/empty, use it directly
            if (backendDescription !== undefined && backendDescription !== null && backendDescription.trim() !== '') {
                setApiDescription(backendDescription);
                // Still fetch title from API
                try {
                    const res = await fetch(`/api/features?identifier=${encodeURIComponent(pageKey)}&locale=${encodeURIComponent(locale)}`, {
                        cache: 'no-store',
                    });
                    if (res.ok) {
                        const data = await res.json();
                        if (data && data.title) {
                            setApiTitle(data.title);
                        }
                    }
                } catch (error) {

                } finally {
                    setIsLoadingApi(false);
                }
                return;
            }

            // Fetch both description and title from API
            try {
                const apiUrl = `/api/features?identifier=${encodeURIComponent(pageKey)}&locale=${encodeURIComponent(locale)}`;

                const res = await fetch(apiUrl, {
                    cache: 'no-store',
                });

                if (res.ok) {
                    const data = await res.json();

                    if (data) {
                        if (data.description && data.description.trim() !== '') {
                            setApiDescription(data.description);

                        } else {

                        }
                        if (data.title && data.title.trim() !== '') {
                            setApiTitle(data.title);

                        }
                    } else {

                    }
                } else {

                }
            } catch (error) {

            } finally {
                setIsLoadingApi(false);
            }
        };

        fetchFeatureData();
    }, [pageKey, locale, backendDescription]);

    // Use backend description if available, otherwise fall back to translations
    const hasBackendDescription = apiDescription !== null && apiDescription.trim() !== '';

    // Split description into paragraphs
    // Always prioritize backend data over translations
    let paragraphs: string[] = [];

    if (hasBackendDescription) {
        // Split by double newlines (explicit paragraph breaks) or single newline if no double newlines
        let splitParagraphs = apiDescription.split(/\n\n+/).filter(p => p.trim());

        // If no double newlines found, try splitting by single newline
        if (splitParagraphs.length <= 1) {
            splitParagraphs = apiDescription.split(/\n+/).filter(p => p.trim());
        }

        // If still single paragraph and content is long, try smart splitting by sentence boundaries
        if (splitParagraphs.length <= 1 && apiDescription.length > 300) {
            // Look for pattern: sentence ending (. ! ?) followed by space and capital letter
            // This often indicates a paragraph break in text without explicit newlines
            const smartSplit = apiDescription.match(/[^.!?]+[.!?]\s+(?=[A-Z])/g);
            if (smartSplit && smartSplit.length > 1) {
                // Reconstruct by finding where sentences end with capital letter starts
                const parts: string[] = [];
                let currentPart = '';
                const sentences = apiDescription.match(/[^.!?]+[.!?]\s*/g) || [];

                for (let i = 0; i < sentences.length; i++) {
                    const sentence = sentences[i];
                    currentPart += sentence;
                    // Check if next sentence starts with capital letter (likely new paragraph)
                    if (i < sentences.length - 1) {
                        const nextSentence = sentences[i + 1];
                        if (nextSentence && /^[A-Z]/.test(nextSentence.trim())) {
                            // Check if current part is substantial enough to be a paragraph
                            if (currentPart.trim().length > 100) {
                                parts.push(currentPart.trim());
                                currentPart = '';
                            }
                        }
                    }
                }
                if (currentPart.trim()) {
                    parts.push(currentPart.trim());
                }

                if (parts.length > 1) {
                    splitParagraphs = parts;
                }
            }
        }

        paragraphs = splitParagraphs.length > 0 ? splitParagraphs : [apiDescription];
    } else if (!isLoadingApi) {
        // Only fallback to translations if API loading is complete and no backend data

        paragraphs = paragraphKeys.map(key => {
            const text = t(key);
            return text && text !== key ? text : null;
        }).filter((text): text is string => text !== null);
    }
    // If still loading, paragraphs will be empty array (shows nothing until loaded)

    return (
        <SectionWrapper variant="default" padding="md" ref={sectionRef.ref} className="relative overflow-hidden bg-white dark:bg-black">
            <div className="container relative z-10">
                {/* Section Heading */}


                {/* Content Grid */}
                <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
                    {/* Left Column - Text Content */}
                    <div
                        className={cn(
                            "transition-all duration-300 ease-out",
                            sectionRef.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-6'
                        )}
                    >
                        {(sectionHeadingPage || sectionHeadingTitle || apiTitle) && (
                            <SectionHeading
                                page={apiTitle ? 'DO_NOT_FETCH' : (sectionHeadingPage || '')}
                                fallbackTitle={apiTitle || sectionHeadingTitle || ''}
                                fallbackPill={sectionHeadingPill}
                                align="left"
                                maxWidthClass="max-w-4xl"
                                className='!mb-8'
                            />
                        )}
                        <div className="space-y-4">
                            {/* Paragraphs */}
                            {paragraphs.map((text, index) => {
                                if (!text || text.trim() === '') return null;

                                return (
                                    <div
                                        key={index}
                                        className={cn(
                                            "transition-all duration-700 ease-out",
                                            sectionRef.isVisible
                                                ? 'opacity-100 translate-y-0'
                                                : 'opacity-0 translate-y-4'
                                        )}
                                        style={{ transitionDelay: `${200 + index * 100}ms` }}
                                    >
                                        <Typography
                                            variant="p"
                                            className="text-gray-600 dark:text-gray-400 leading-relaxed text-base"
                                        >
                                            {text}
                                        </Typography>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Right Column - Image */}
                    <div
                        className={cn(
                            "relative transition-all duration-700 ease-out",
                            sectionRef.isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-6'
                        )}
                        style={{ transitionDelay: '300ms' }}
                    >
                        <div className="relative rounded-2xl overflow-hidden shadow-lg">
                            <Image
                                src={imageSrc}
                                alt={imageAlt || 'Feature illustration'}
                                width={600}
                                height={500}
                                className="w-full h-full object-cover rounded-2xl"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </SectionWrapper>
    );
}
